/**
 * Auth session management service.
 *
 * This service handles session-related auth operations that require
 * cookie management and Sentry context. It wraps the auto-generated
 * auth service with these side effects.
 */

import type { RefreshTokenResponse } from '@compass/client/types/auth';
import { createCookieKey } from '@utils/user';
import Cookies from 'js-cookie';

import { clearSentryUser } from '@/config/sentry';

import { authService } from './api';
import { emitTokenRefreshed } from './auth.events';

/**
 * Refreshes the access token and updates the cookie.
 * Returns the new access token response or null if refresh fails.
 */
export async function refreshAccessToken(): Promise<RefreshTokenResponse | null> {
  try {
    const cookieKey = createCookieKey();
    const userData = Cookies.get(cookieKey);

    if (!userData) {
      console.debug('No user data in cookie for refresh');
      return null;
    }

    const parsed = JSON.parse(userData);

    // NOTE: refresh_token is HttpOnly cookie. Browser automatically sends it with the request
    console.debug('Calling refresh token endpoint...');
    const response = await authService.refreshToken({});

    if (response.access_token) {
      // Update the access token in cookie while keeping other data
      const updatedUserData = {
        ...parsed,
        access_token: response.access_token,
      };

      Cookies.set(cookieKey, JSON.stringify(updatedUserData), {
        expires: 7, // 7 days
        path: '/',
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
      });

      // Tell holders of an out-of-band copy of the token (the heartbeat worker) to
      // re-read the cookie. Without this they keep replaying the dead token.
      emitTokenRefreshed();

      return response;
    }

    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // If refresh fails, the user will need to log in again
    return null;
  }
}

/**
 * Logs out the user by calling the API and clearing local auth state.
 * Clears the auth cookie and Sentry user context.
 */
export async function logout(): Promise<void> {
  try {
    // Call logout endpoint to invalidate server-side session
    await authService.logout({});
  } catch (error) {
    // Logout errors shouldn't prevent client-side cleanup
    console.error('Logout API call failed:', error);
  }

  // Always clear client-side auth
  const cookieKey = createCookieKey();
  Cookies.remove(cookieKey, { path: '/' });

  // Clear Sentry user context
  clearSentryUser();
}

/**
 * Gets the current access token from the cookie.
 */
export function getToken(): string | null {
  try {
    const cookieKey = createCookieKey();
    const userData = Cookies.get(cookieKey);
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.access_token || null;
    }
  } catch (error) {
    console.error('Error reading auth token:', error);
  }
  return null;
}

/**
 * Checks if the user is authenticated (has a valid token in cookie).
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}
